import useSWR from 'swr';

import type ApiCaller from '@campaign-manager/core/infra/repo/rmt-api/ApiCaller';
import type { EnvelopeApiCaller } from '@campaign-manager/core/infra/repo/rmt-api/ApiCaller';
import { Response } from '@campaign-manager/core/infra/repo/rmt-api/Response';

import { swrDefaults } from '../../swrDefaults';
import { Fetcher } from './Fetcher';
import type RepoCallHookOptions from './RepoCallHookOptions';

/**
 * The two overloads make the hook's declared type answerable to the caller's: an
 * envelope caller reads as its unwrapped payload, any other as what it resolves to.
 * A type that does not match the endpoint fails to resolve either overload.
 */
export default function useGet<D>(
  caller: EnvelopeApiCaller<D>,
  opts: RepoCallHookOptions,
): Response<D>;
export default function useGet<T>(caller: ApiCaller<T>, opts: RepoCallHookOptions): Response<T>;
export default function useGet<T>(caller: ApiCaller, opts: RepoCallHookOptions) {
  const { skip, suspense, query, path } = opts || {};
  const callerOptions = {
    query,
    path,
  };

  const fetcher = new Fetcher(caller);

  const {
    data: resp,
    error,
    isLoading,
    mutate,
  } = useSWR<T, Error>(
    skip ? null : fetcher.getKey(callerOptions),
    () => fetcher.call(callerOptions),
    { ...swrDefaults, suspense },
  );

  return Response.buildResponse<T>(resp, caller.returnsEnvelope, error, isLoading, mutate);
}
